import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ClassNames from "classnames";

import "./Carousel.scss";

/**
 * Component that shows a Carousel
 */
class Carousel extends Component {
   constructor(props) {
      super(props);

      this.state = {
         currentSlideIndex: 0
      }

      this.prev = this.prev.bind(this);
      this.next = this.next.bind(this);
      this.navigateTo = this.navigateTo.bind(this);
   }
   /**
    * Navigate to the previous slide of the carousel.
    */
   prev() {
      this.navigateTo(this.state.currentSlideIndex - 1);
   }
   /**
    * Navigate to the next slide of the carousel.
    */
   next() {
      this.navigateTo(this.state.currentSlideIndex + 1);
   }
   /**
    * Navigate to a specific slide of the carousel.
    * @param index - The slide index
    */
   navigateTo(index) {
      const numberOfSlides = React.Children.count(this.props.children);

      var nextSlidesEnded = (index >= numberOfSlides);
      var prevSlidesEnded = (index < 0);

      var currentSlideIndex = index;

      if (!this.props.endlessly && (nextSlidesEnded || prevSlidesEnded))
         return;

      if (this.props.endlessly) {
                              // Go back to the first slide when all slides have been displayed
                              // by navigating using the next button.
         if (nextSlidesEnded)
            currentSlideIndex = 0;

                              // Go to the last slide when all slides have been displayed
                              // by navigating using the previous button.
         if (prevSlidesEnded)
            currentSlideIndex = numberOfSlides - 1;
      }

      this.setState({ currentSlideIndex });
   }
   /**
    * renders the slides elements
    */
   renderSlides() {
      return (
         React.Children.map(this.props.children, (slide, index) => {
            const slideClassNames = ClassNames(
               "carousel-slide carousel-slide--fade",
               { "carousel-slide--hidden": (this.state.currentSlideIndex !== index) }
            );

            return (<div key={index} className={slideClassNames}>{ slide }</div>);
         })
      );
   }
   /**
    * renders the navigation buttons
    * @param numberOfSlides - The number of slides displayed in the carousel
    */
   renderNavigationButtons(numberOfSlides) {
      const disabledPrevBtn = (this.state.currentSlideIndex === 0 && !this.props.endlessly);
      const disabledNextBtn = ((this.state.currentSlideIndex === numberOfSlides - 1) && !this.props.endlessly);
      const displayOnDoneBtn = (disabledNextBtn && this.props.doneText && this.props.onDone);

      return (
         <React.Fragment>
            {
               numberOfSlides > 1 &&
               <button
                  className="carousel-navigation__prev"
                  onClick={this.prev}
                  disabled={disabledPrevBtn}>{this.props.prevText}</button>
            }

            <div className="carousel-navigation__dots">{ this.renderDots(numberOfSlides) }</div>

            {
               !displayOnDoneBtn &&
               <button
                  className="carousel-navigation__next"
                  onClick={this.next}
                  disabled={disabledNextBtn}>{this.props.nextText}</button>
            }

            {
               displayOnDoneBtn &&
               <button
                  className="carousel-navigation__done"
                  onClick={() => this.props.onDone()}>
                  {this.props.doneText}
               </button>
            }
         </React.Fragment>
      );
   }
   /**
    * renders the navigation dots buttons
    * @param numberOfSlides - The number of slides displayed in the carousel
    */
   renderDots(numberOfSlides) {
      // do not display navigation dots when there is only one slide
      if (numberOfSlides <= 1)
         return (null);

      return (
         [...Array(numberOfSlides).keys()].map(index => {
            const dotClassNames = ClassNames(
               "carousel-dot",
               { "carousel-dot--active": (this.state.currentSlideIndex === index) }
            );

            return (
               <span
                  key={index}
                  className={dotClassNames}
                  onClick={() => this.navigateTo(index) }></span>
            );
         })
      );
   }
   /**
    * renders the Carousel component
    */
   render() {
      const numberOfSlides = React.Children.count(this.props.children);

      if (!numberOfSlides)
         return (null);

      return (
         <div className={'carousel ' + this.props.className}>
            <div className="carousel-slides">
               { this.renderSlides() }
            </div>
            <div className="carousel-navigation">
               { this.renderNavigationButtons(numberOfSlides) }
            </div>
         </div>
      );
   }
}

Carousel.propTypes = {
   /**
    * defines the Carousel container css classnames
    */
   className: PropTypes.string,
   /**
    * defines the Carousel done navigation button text
    */
   doneText: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.string
   ]),
   /**
    * A flag to enable/disabled the endlessly navigation behavior.
    * The default value is false.
    */
   endlessly: PropTypes.bool,
   /**
    * defines the Carousel next navigation button text
    */
   nextText: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.string
   ]),
   /**
    * callback called when the done button is clicked
    */
   onDone: PropTypes.func,
   /**
    * defines the Carousel previous navigation button text
    */
   prevText: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.string
   ])
}

Carousel.defaultProps = {
   endlessly: false
};

export default Carousel;
